import { type Reducer, useReducer } from 'react';
import { type SortingOrder } from '../../../api/types';

export type PageState = {
    page: number;
    pageSize: number;
};

export type SortingRule<SortField> = {
    field: SortField;
    order: SortingOrder;
};

export type FilteringRule<FilterField> = {
    [key in keyof FilterField]: FilterField[key] | undefined;
};

export type State<SortField, FilterField> = PageState & {
    sort?: SortingRule<SortField>;
    filter?: FilteringRule<FilterField>;
} & { scroll: boolean };

export type ActionTypes = {
    SetSort: 'setSort';
};

export type SetSortAction<SortField> = { type: 'setSort'; sortBy: SortingRule<SortField> };
export type SetFilterAction<FilterField> = { type: 'setFilter'; filteringRule: FilteringRule<FilterField> };
export type SetPageAction = { type: 'setPage'; page: number };
export type SetPageSizeAction = { type: 'setPageSize'; pageSize: number };
export type SetScrollAction = { type: 'setScroll'; scroll: boolean };

export type Action<SortField, FilterField> =
    | SetPageAction
    | SetPageSizeAction
    | SetSortAction<SortField>
    | SetFilterAction<FilterField>
    | SetScrollAction;

const reducer = <SortField, FilterField>(
    state: State<SortField, FilterField>,
    action: Action<SortField, FilterField>
): State<SortField, FilterField> => {
    switch (action.type) {
        case 'setPage':
            return { ...state, page: action.page, scroll: true };

        case 'setPageSize':
            return { ...state, page: 1, pageSize: action.pageSize, scroll: false };

        case 'setSort':
            return { ...state, page: 1, scroll: false, sort: action.sortBy };

        case 'setFilter':
            return { ...state, page: 1, scroll: false, filter: action.filteringRule };

        case 'setScroll':
            return { ...state, scroll: action.scroll };

        default:
            return state;
    }
};

type InitialStateCIReducer<SortField, FilterField> = Partial<PageState> & {
    sort: SortingRule<SortField>;
    filter: FilteringRule<FilterField>;
} & { scroll?: boolean };

type CIReducer<SortField, FilterField> = Reducer<State<SortField, FilterField>, Action<SortField, FilterField>>;

const defaultInitialState = {
    page: 1,
    pageSize: 18,
    scroll: false,
};

export const useListCIReducer = <SortField, FilterField>(initialState: InitialStateCIReducer<SortField, FilterField>) =>
    useReducer<CIReducer<SortField, FilterField>>(reducer, {
        ...defaultInitialState,
        ...initialState,
    });
